/**
 * Struktura organizacyjna — działy (ADR-0057, faza 4).
 *
 * Kafelek konfiguratora „Struktura organizacyjna / Działy, zespoły i stanowiska"
 * jest w makiecie BEZCZYNNY, a status ma wpisany na sztywno. U nas status liczy
 * się z bazy i mówił prawdę — że struktury nie ma — tyle że nie dawało się jej
 * założyć inaczej niż zatrudniając człowieka: jedyny zapis działu siedział
 * wewnątrz przyjęcia zaproszenia, z wolnego tekstu. Ekran zamyka tę lukę,
 * a przy okazji pozwala poprawić literówkę, która wcześniej zakładała nowy dział.
 */
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Card, EmptyState, FormField, Input, Settings, Spinner } from '@k1p4/ui'
import { ApiError, api } from '../../api/client'
import styles from './Structure.module.css'

const KLUCZ = ['config', 'departments'] as const

export function Structure() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [nowa, setNowa] = useState('')
  const [edytowany, setEdytowany] = useState<string | null>(null)
  const [nazwaEdycji, setNazwaEdycji] = useState('')
  const [blad, setBlad] = useState<string | null>(null)
  const idNowa = useId()

  const dzialy = useQuery({ queryKey: KLUCZ, queryFn: () => api.departments() })

  /** Każdy błąd serwera ma tu SWOJE zdanie — „nie udało się" nic nie tłumaczy. */
  function pokazBlad(caught: unknown): void {
    if (caught instanceof ApiError && caught.code === 'DEPARTMENT_EXISTS') {
      setBlad('structure.errorExists')
    } else if (caught instanceof ApiError && caught.code === 'DEPARTMENT_IN_USE') {
      setBlad('structure.errorInUse')
    } else {
      setBlad('common.error')
    }
  }

  async function odswiez(): Promise<void> {
    setBlad(null)
    await queryClient.invalidateQueries({ queryKey: KLUCZ })
  }

  const dodaj = useMutation({
    mutationFn: (name: string) => api.createDepartment(name),
    onSuccess: async () => {
      setNowa('')
      await odswiez()
    },
    onError: pokazBlad,
  })

  const zmien = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => api.renameDepartment(id, name),
    onSuccess: async () => {
      setEdytowany(null)
      await odswiez()
    },
    onError: pokazBlad,
  })

  const usun = useMutation({
    mutationFn: (id: string) => api.deleteDepartment(id),
    onSuccess: odswiez,
    onError: pokazBlad,
  })

  if (dzialy.isPending) return <Spinner label={t('common.loading')} />
  if (dzialy.isError) {
    return <EmptyState title={t('common.error')} description={t('common.retry')} />
  }

  const items = dzialy.data.items

  return (
    <>
      <p className={styles.lead}>{t('structure.lead')}</p>

      {blad !== null ? (
        <p className={styles.error} role="alert">
          {t(blad)}
        </p>
      ) : null}

      <Card pad="lg" className={styles.addCard}>
        <form
          className={styles.addForm}
          onSubmit={(event) => {
            event.preventDefault()
            if (nowa.trim().length < 2 || dodaj.isPending) return
            dodaj.mutate(nowa.trim())
          }}
        >
          <FormField label={t('structure.nameLabel')} htmlFor={idNowa} className={styles.field}>
            <Input
              id={idNowa}
              placeholder={t('structure.namePlaceholder')}
              value={nowa}
              onChange={(event) => setNowa(event.target.value)}
            />
          </FormField>
          <Button type="submit" disabled={nowa.trim().length < 2 || dodaj.isPending}>
            {t('structure.add')}
          </Button>
        </form>
      </Card>

      <Card pad="lg">
        {items.length === 0 ? (
          <EmptyState
            icon={<Settings size={26} />}
            title={t('structure.empty')}
            description={t('structure.emptyHint')}
          />
        ) : (
          <ul className={styles.list}>
            {items.map((dzial) => (
              <li key={dzial.id} className={styles.row}>
                {edytowany === dzial.id ? (
                  <form
                    className={styles.editForm}
                    onSubmit={(event) => {
                      event.preventDefault()
                      if (nazwaEdycji.trim().length < 2) return
                      zmien.mutate({ id: dzial.id, name: nazwaEdycji.trim() })
                    }}
                  >
                    <FormField
                      label={t('structure.nameLabel')}
                      htmlFor={`${idNowa}-${dzial.id}`}
                      className={styles.field}
                    >
                      <Input
                        id={`${idNowa}-${dzial.id}`}
                        value={nazwaEdycji}
                        onChange={(event) => setNazwaEdycji(event.target.value)}
                      />
                    </FormField>
                    <Button type="submit" size="sm" disabled={zmien.isPending}>
                      {t('structure.save')}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setEdytowany(null)
                        setBlad(null)
                      }}
                    >
                      {t('structure.cancel')}
                    </Button>
                  </form>
                ) : (
                  <>
                    <div className={styles.identity}>
                      <p className={styles.name}>{dzial.name}</p>
                      <p className={styles.meta}>
                        {dzial.employees === 0
                          ? t('structure.peopleNone')
                          : t('structure.people', { count: dzial.employees })}
                      </p>
                    </div>
                    <div className={styles.actions}>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setEdytowany(dzial.id)
                          setNazwaEdycji(dzial.name)
                          setBlad(null)
                        }}
                      >
                        {t('structure.rename')}
                      </Button>
                      {/*
                        Usuwanie wyłącznie pustego działu. Przypisanie jest faktem
                        z warunków umowy, a nie etykietą do wyczyszczenia — ludzi
                        przenosi człowiek, świadomie.
                      */}
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={dzial.employees > 0 || usun.isPending}
                        onClick={() => usun.mutate(dzial.id)}
                      >
                        {t('structure.remove')}
                      </Button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  )
}
